import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const styles = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  badge: {
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 10px',
    borderRadius: '12px',
    display: 'inline-block'
  },
  badgeHealthy: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  badgeError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  section: {
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6'
  },
  sectionLast: {
    padding: '12px 0',
    borderBottom: 'none'
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  statusIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  statusPass: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  statusFail: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  statusWarn: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  labelText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151'
  },
  countText: {
    fontSize: '14px',
    color: '#6b7280'
  },
  errorList: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    border: '1px solid #fecaca'
  },
  errorItem: {
    fontSize: '13px',
    color: '#991b1b',
    padding: '4px 0',
    lineHeight: '1.4'
  },
  warningList: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#fffbeb',
    borderRadius: '6px',
    border: '1px solid #fed7aa'
  },
  warningItem: {
    fontSize: '13px',
    color: '#92400e',
    padding: '4px 0',
    lineHeight: '1.4'
  },
  suggestionList: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#eff6ff',
    borderRadius: '6px',
    border: '1px solid #bfdbfe'
  },
  suggestionItem: {
    fontSize: '13px',
    color: '#1e40af',
    padding: '4px 0',
    lineHeight: '1.4'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px'
  },
  errorContainer: {
    padding: '20px',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '14px',
    textAlign: 'center'
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#6b7280'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  refreshButton: {
    fontSize: '12px',
    color: '#6b7280',
    background: 'none',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '4px 10px',
    cursor: 'pointer'
  }
};

const StatusIcon = ({ status }) => {
  if (status === 'pass') {
    return (
      <span style={{ ...styles.statusIcon, ...styles.statusPass }}>
        &#10003;
      </span>
    );
  }
  if (status === 'fail') {
    return (
      <span style={{ ...styles.statusIcon, ...styles.statusFail }}>
        &#10007;
      </span>
    );
  }
  return (
    <span style={{ ...styles.statusIcon, ...styles.statusWarn }}>
      !
    </span>
  );
};

const SetupChecklist = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHealthCheck = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/admin/setup-validation/health-check');
      setHealthData(response.data);
    } catch (err) {
      setError(err.response?.data?.msg || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthCheck();
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>Loading configuration health check...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          Failed to load health check: {error}
        </div>
      </div>
    );
  }

  if (!healthData || !healthData.summary) {
    return null;
  }

  const { summary, errors: healthErrors, warnings: healthWarnings, suggestions: healthSuggestions } = healthData;
  const counts = summary.counts || {};

  const hasErrors = healthErrors && healthErrors.length > 0;
  const hasWarnings = healthWarnings && healthWarnings.length > 0;
  const hasSuggestions = healthSuggestions && healthSuggestions.length > 0;

  const overallBadgeStyle = hasErrors
    ? { ...styles.badge, ...styles.badgeError }
    : hasWarnings
      ? { ...styles.badge, ...styles.badgeWarning }
      : { ...styles.badge, ...styles.badgeHealthy };

  const overallLabel = hasErrors
    ? 'Issues Found'
    : hasWarnings
      ? 'Warnings'
      : 'Healthy';

  const getItemStatus = (count, label) => {
    if (count > 0) return 'pass';
    // Some items are optional (triage, rules) so just warn
    if (label === 'Personas' || label === 'Onboarding Flows') return 'fail';
    return 'warn';
  };

  const checklistItems = [
    { label: 'Personas', count: counts.personas || 0 },
    { label: 'Onboarding Flows', count: counts.onboardingFlows || 0 },
    { label: 'Triage Strategies', count: counts.triageStrategies || 0 },
    { label: 'Assignment Rules', count: counts.assignmentRules || 0 },
    { label: 'Active Invitations', count: counts.activeInvitations || 0 }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Platform Configuration Health</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button style={styles.refreshButton} onClick={fetchHealthCheck}>
            Refresh
          </button>
          <span style={overallBadgeStyle}>{overallLabel}</span>
        </div>
      </div>

      {checklistItems.map((item, idx) => {
        const isLast = idx === checklistItems.length - 1;
        const status = getItemStatus(item.count, item.label);

        return (
          <div key={item.label} style={isLast ? styles.sectionLast : styles.section}>
            <div style={styles.sectionRow}>
              <div style={styles.sectionLabel}>
                <StatusIcon status={status} />
                <span style={styles.labelText}>{item.label}</span>
              </div>
              <span style={styles.countText}>{item.count} active</span>
            </div>
          </div>
        );
      })}

      {hasErrors && (
        <div style={styles.errorList}>
          <div style={{ fontWeight: '600', fontSize: '13px', color: '#991b1b', marginBottom: '6px' }}>
            Broken References ({healthErrors.length})
          </div>
          {healthErrors.map((err, idx) => (
            <div key={idx} style={styles.errorItem}>
              [{err.entity}] {err.message}
            </div>
          ))}
        </div>
      )}

      {hasWarnings && (
        <div style={styles.warningList}>
          <div style={{ fontWeight: '600', fontSize: '13px', color: '#92400e', marginBottom: '6px' }}>
            Warnings ({healthWarnings.length})
          </div>
          {healthWarnings.map((warn, idx) => (
            <div key={idx} style={styles.warningItem}>
              [{warn.entity}] {warn.message}
            </div>
          ))}
        </div>
      )}

      {hasSuggestions && (
        <div style={styles.suggestionList}>
          <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e40af', marginBottom: '6px' }}>
            Suggestions ({healthSuggestions.length})
          </div>
          {healthSuggestions.map((sug, idx) => (
            <div key={idx} style={styles.suggestionItem}>
              {sug.message}
            </div>
          ))}
        </div>
      )}

      <div style={styles.summaryRow}>
        <span style={styles.summaryItem}>
          <strong>{summary.totalChecks}</strong> checks
        </span>
        <span style={styles.summaryItem}>
          <strong style={{ color: '#065f46' }}>{summary.passed}</strong> passed
        </span>
        <span style={styles.summaryItem}>
          <strong style={{ color: '#991b1b' }}>{summary.failed}</strong> failed
        </span>
        <span style={styles.summaryItem}>
          <strong style={{ color: '#92400e' }}>{summary.warnings}</strong> warnings
        </span>
      </div>
    </div>
  );
};

export default SetupChecklist;
